import { Field } from "@base-ui/react/field";
import { Popover } from "@base-ui/react/popover";
import {
  addDays,
  addMonths,
  addYears,
  eachDayOfInterval,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subYears,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { vars } from "../../theme/theme.css";
import { fillDaysArray } from "../../utils/functions";
import { Button } from "../Button";
import {
  errorSlotClass,
  errorSlotInnerClass,
  fieldInfoContainerClass,
} from "../FieldInfo/styles.css";
import { IconArrowDropDown, IconCalendarCheck } from "../icons";
import { Spinner } from "../Spinner";
import { CalendarInputs } from "./CalendarComponents";
import * as styles from "./styles.css";

export interface DateSelected {
  start: Date;
  end: Date;
}

interface SingleCalendarProps extends React.HTMLAttributes<HTMLButtonElement> {
  onlyDate?: boolean;
  onSelectDate?: (date: Date) => void;
  defaultDate?: Date;
  isLoading?: boolean;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  value?: Date;
  containerStyles?: React.CSSProperties;
  label?: React.ReactNode;
  error?: string;
}

const DayButtonComponent = React.memo(
  function DayButton({
    $isSelected,
    $isHovered,
    $isFirst,
    $isLast,
    children,
    className,
    ...props
  }: any) {
    const classNames = [styles.dayButton];

    if ($isSelected) {
      classNames.push(styles.dayButtonSelected);
    }
    if ($isHovered) {
      classNames.push(styles.dayButtonHovered);
    }
    if ($isFirst || $isLast) {
      classNames.push(styles.dayButtonFirstOrLast);
    }

    return (
      <button className={classNames.join(" ")} {...props}>
        {children}
      </button>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.$isSelected === nextProps.$isSelected &&
      prevProps.disabled === nextProps.disabled &&
      prevProps.children === nextProps.children
    );
  },
);

const DayButtonMemo = React.memo(DayButtonComponent);

export const SingleCalendar = forwardRef(function SingleCalendar(
  {
    onlyDate,
    onSelectDate,
    defaultDate,
    isLoading,
    disabled,
    minDate,
    maxDate,
    value,
    containerStyles,
    label,
    error,
    ...props
  }: SingleCalendarProps,
  ref: React.Ref<HTMLDivElement>,
) {
  const [show, setShow] = useState(false);

  const defaultDateValue = useMemo(() => endOfDay(new Date()), []);

  const [selectedDate, setSelectedDate] = useState<Date>(
    defaultDate || defaultDateValue,
  );
  const [dateOnButton, setDateOnButton] = useState<Date>(
    defaultDate || defaultDateValue,
  );

  const [monthToShow, setMonthToShow] = useState({
    start: startOfMonth(selectedDate),
    end: endOfMonth(selectedDate),
  });

  const [focusedDate, setFocusedDate] = useState<Date>(selectedDate);
  const gridRef = useRef<HTMLDivElement>(null);
  const focusPendingRef = useRef(false);

  useLayoutEffect(() => {
    if (!focusPendingRef.current) return;
    focusPendingRef.current = false;
    const btn = gridRef.current?.querySelector<HTMLButtonElement>(
      `[data-date="${format(focusedDate, "yyyy-MM-dd")}"]`,
    );
    btn?.focus({ preventScroll: true });
  });

  const daysToShowMemo = useMemo(() => {
    const daysOfTheMonth = eachDayOfInterval(monthToShow);
    fillDaysArray(daysOfTheMonth);

    return daysOfTheMonth.map((day) => ({
      value: day,
      day: day.getDate(),
      month: day.getMonth(),
      year: day.getFullYear(),
      weekday: day.getDay(),
    }));
  }, [monthToShow]);

  const toggleShow = useCallback(() => {
    setSelectedDate(dateOnButton);
    setShow((state) => !state);
  }, [dateOnButton]);

  const handleSelectDate = useCallback(
    (date: Date) => {
      onSelectDate?.(date);
      setDateOnButton(date);
      setShow(false);
    },
    [onSelectDate],
  );

  const onClickDay = useCallback(
    (day: Date) => {
      setFocusedDate(day);
      if (onlyDate) {
        handleSelectDate(day);
        return;
      }
      setSelectedDate(endOfDay(day));
    },
    [onlyDate, handleSelectDate],
  );

  const handlePreviousMonth = useCallback(() => {
    const newMonth = subMonths(monthToShow.start, 1);
    setMonthToShow({
      start: startOfMonth(newMonth),
      end: endOfMonth(newMonth),
    });
  }, [monthToShow.start]);

  const handleNextMonth = useCallback(() => {
    const newMonth = addMonths(monthToShow.start, 1);
    setMonthToShow({
      start: startOfMonth(newMonth),
      end: endOfMonth(newMonth),
    });
  }, [monthToShow.start]);

  useEffect(() => {
    if (value) {
      setDateOnButton(value);
      setSelectedDate(value);
      setFocusedDate(value);
    }
  }, [value]);

  useEffect(() => {
    setMonthToShow({
      start: startOfMonth(selectedDate),
      end: endOfMonth(selectedDate),
    });
  }, [selectedDate]);

  const handleGridKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const NAV_KEYS = [
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "PageUp",
        "PageDown",
        "Home",
        "End",
        "Enter",
        " ",
      ];
      if (!NAV_KEYS.includes(e.key)) return;
      e.preventDefault();

      if (e.key === "Enter" || e.key === " ") {
        onClickDay(focusedDate);
        return;
      }

      let next = focusedDate;
      switch (e.key) {
        case "ArrowLeft":
          next = subDays(focusedDate, 1);
          break;
        case "ArrowRight":
          next = addDays(focusedDate, 1);
          break;
        case "ArrowUp":
          next = subDays(focusedDate, 7);
          break;
        case "ArrowDown":
          next = addDays(focusedDate, 7);
          break;
        case "PageUp":
          next = e.shiftKey
            ? subYears(focusedDate, 1)
            : subMonths(focusedDate, 1);
          break;
        case "PageDown":
          next = e.shiftKey
            ? addYears(focusedDate, 1)
            : addMonths(focusedDate, 1);
          break;
        case "Home":
          next = startOfWeek(focusedDate);
          break;
        case "End":
          next = endOfWeek(focusedDate);
          break;
      }

      if (minDate && isBefore(next, startOfDay(minDate)))
        next = startOfDay(minDate);
      if (maxDate && isAfter(next, endOfDay(maxDate))) next = endOfDay(maxDate);

      focusPendingRef.current = true;
      setFocusedDate(next);
      if (!isSameMonth(next, monthToShow.start)) {
        setMonthToShow({
          start: startOfMonth(next),
          end: endOfMonth(next),
        });
      }
    },
    [focusedDate, monthToShow.start, minDate, maxDate, onClickDay],
  );

  return (
    <Field.Root
      invalid={!!error}
      className={styles.datePickerWrapper}
      style={containerStyles}
      ref={ref as React.Ref<HTMLDivElement>}
    >
      {label && <Field.Label>{label}</Field.Label>}

      <Popover.Root open={show} onOpenChange={setShow}>
        <Popover.Trigger
          render={
            <button
              className={styles.datePickerTrigger}
              type="button"
              disabled={disabled || isLoading}
              {...props}
            />
          }
          onClick={toggleShow}
        >
          {format(dateOnButton, `dd/MM/yyyy${onlyDate ? "" : "- HH:mm"}`)}
          {isLoading ? (
            <Spinner size={20} color={vars.colors.neutral.light} />
          ) : (
            <IconCalendarCheck />
          )}
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Positioner
            side="bottom"
            align="start"
            sideOffset={4}
            className={styles.datePickerPositioner}
          >
            <Popover.Popup
              initialFocus={false}
              finalFocus={false}
              className={styles.datePickerContent}
            >
              <div className={styles.contentBody}>
                <div className={styles.calendarContainer}>
                  <div className={styles.monthYearHeader}>
                    <IconArrowDropDown
                      fontSize={24}
                      style={{ transform: "rotate(90deg)" }}
                      onClick={handlePreviousMonth}
                    />
                    <span>
                      {format(monthToShow.start, "MMMM", { locale: ptBR })}
                      {", "}
                      {format(monthToShow.start, "yyyy")}
                    </span>
                    <IconArrowDropDown
                      fontSize={24}
                      style={{ transform: "rotate(-90deg)" }}
                      onClick={handleNextMonth}
                    />
                  </div>
                  <ul>
                    <li>D</li>
                    <li>S</li>
                    <li>T</li>
                    <li>Q</li>
                    <li>Q</li>
                    <li>S</li>
                    <li>S</li>
                  </ul>
                  <div
                    className={styles.styledCalendar}
                    ref={gridRef}
                    role="grid"
                    aria-label="Calendário"
                    onKeyDown={handleGridKeyDown}
                  >
                    {daysToShowMemo.map((day) => (
                      <DayButtonMemo
                        key={day.value.toString()}
                        data-date={format(day.value, "yyyy-MM-dd")}
                        tabIndex={isSameDay(day.value, focusedDate) ? 0 : -1}
                        aria-label={format(day.value, "d 'de' MMMM 'de' yyyy", {
                          locale: ptBR,
                        })}
                        aria-selected={isSameDay(
                          endOfDay(day.value),
                          selectedDate,
                        )}
                        onClick={() => onClickDay(day.value)}
                        $isSelected={isSameDay(
                          endOfDay(day.value),
                          selectedDate,
                        )}
                        disabled={
                          (minDate && isBefore(day.value, minDate)) ||
                          (maxDate && isAfter(day.value, maxDate))
                        }
                      >
                        {day.day}
                      </DayButtonMemo>
                    ))}
                  </div>

                  {!onlyDate && (
                    <CalendarInputs
                      selectedDate={selectedDate}
                      setSelectedDate={setSelectedDate}
                    />
                  )}
                </div>
              </div>

              {!onlyDate && (
                <div className={styles.contentFooter}>
                  <Button
                    variant="outlined"
                    onClick={() => handleSelectDate(selectedDate)}
                  >
                    Aplicar
                  </Button>
                </div>
              )}
            </Popover.Popup>
          </Popover.Positioner>
        </Popover.Portal>
      </Popover.Root>

      <div className={errorSlotClass} data-visible={error ? "true" : "false"}>
        <div className={errorSlotInnerClass}>
          <Field.Error
            className={fieldInfoContainerClass}
            style={{
              color: vars.colors.red.default,
              opacity: 1,
              transform: "none",
            }}
          >
            {error}
          </Field.Error>
        </div>
      </div>
    </Field.Root>
  );
});

SingleCalendar.displayName = "SingleCalendar";
